import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from "date-fns";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  User,
  MapPin,
  MoreHorizontal,
  X,
  Upload,
  Download,
  Trash2,
  File,
  FolderOpen,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Printer,
  Users,
  FileText,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AppointmentAssessmentButton, { APPT_KIND_COLOR, APPT_KIND_LABEL } from "@/components/assessments/AppointmentAssessmentButton";
import VisitClockButton from "@/components/assessments/VisitClockButton";
import { QuarterlyMeetingDialog } from "@/components/client-detail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const appointmentTypes = [
  { value: "visit", label: "Home Visit" },
  { value: "assessment", label: "Assessment" },
  { value: "follow_up", label: "Follow Up" },
  { value: "md_appointment", label: "MD Appointment" },
];

const appointmentTypeColors = {
  visit: "bg-emerald-100 text-emerald-700 border-emerald-200",
  assessment: "bg-blue-100 text-blue-700 border-blue-200",
  follow_up: "bg-purple-100 text-purple-700 border-purple-200",
  md_appointment: "bg-amber-100 text-amber-700 border-amber-200",
  supervisory: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function SchedulePage() {
  const { user } = useAuth();
  const initialDate = (() => { const d = new URLSearchParams(window.location.search).get("date"); return d ? parseISO(d) : null; })();
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    client_id: "",
    employee_id: "",
    appointment_type: "visit",
    scheduled_date: "",
    scheduled_time: "09:00",
    duration_minutes: 60,
    notes: "",
  });

  // Service Order Validation State
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [clientUsage, setClientUsage] = useState(null);

  // Quarterly Meeting state (Admin only)
  const [quarterlyMeetings, setQuarterlyMeetings] = useState([]);
  const [quarterlyDialogOpen, setQuarterlyDialogOpen] = useState(false);
  const [savingQuarterly, setSavingQuarterly] = useState(false);
  const [editingQuarterlyId, setEditingQuarterlyId] = useState(null);
  const [newQuarterlyMeeting, setNewQuarterlyMeeting] = useState({
    meeting_date: new Date().toISOString().split("T")[0],
    meeting_time: "10:00",
    meeting_type: "Quarterly Meeting",
    presenter: "",
    method_of_presentation: "",
    subjects_covered: "",
    topics_discussed: "",
    reaction_from_participants: "",
    summary_and_conclusions: "",
    action_items: "",
    attendees: "",
    attendees_list: [{ name: "", title: "" }],
    notes: "",
    next_meeting_date: ""
  });
  const [quarterlyFile, setQuarterlyFile] = useState(null);
  const [uploadingQuarterlyDoc, setUploadingQuarterlyDoc] = useState(null);
  const quarterlyFileRef = useRef(null);

  useEffect(() => {
    fetchData();
    if (user?.role === "admin") {
      fetchQuarterlyMeetings();
    }
  }, [currentDate, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentDate), "yyyy-MM-dd");
      const end = format(endOfMonth(currentDate), "yyyy-MM-dd");

      const [appointmentsRes, clientsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/appointments?start_date=${start}&end_date=${end}`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/employees`),
      ]);

      setAppointments(appointmentsRes.data);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error("Failed to fetch schedule data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Quarterly Meeting functions (Admin only)
  const fetchQuarterlyMeetings = async () => {
    try {
      const response = await axios.get(`${API}/quarterly-meetings`);
      setQuarterlyMeetings(response.data);
    } catch (error) {
      console.error("Failed to fetch quarterly meetings:", error);
    }
  };

  const handleCreateQuarterlyMeeting = async () => {
    if (!newQuarterlyMeeting.meeting_date) {
      toast.error("Please select a meeting date");
      return;
    }

    setSavingQuarterly(true);
    try {
      await axios.post(`${API}/quarterly-meetings`, newQuarterlyMeeting);
      toast.success("Quarterly meeting scheduled!");
      setQuarterlyDialogOpen(false);
      resetQuarterlyForm();
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to create quarterly meeting:", error);
      toast.error("Failed to schedule meeting");
    } finally {
      setSavingQuarterly(false);
    }
  };

  const resetQuarterlyForm = () => {
    setNewQuarterlyMeeting({
      meeting_date: new Date().toISOString().split("T")[0],
      meeting_time: "10:00",
      meeting_type: "Quarterly Meeting",
      presenter: user?.name || "",
      method_of_presentation: "",
      subjects_covered: "",
      topics_discussed: "",
      reaction_from_participants: "",
      summary_and_conclusions: "",
      action_items: "",
      attendees: "",
      attendees_list: [{ name: "", title: "" }],
      notes: "",
      next_meeting_date: ""
    });
    setEditingQuarterlyId(null);
  };

  // Print quarterly meeting report
  const handlePrintQuarterlyMeeting = (meeting) => {
    const attendees = meeting.attendees_list || [];
    const printContent = `
      <html>
        <head>
          <title>Summary Report of Meeting</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; }
            .company-name { font-size: 24px; font-weight: bold; color: #0d9488; margin-bottom: 5px; }
            .report-title { font-size: 18px; font-weight: bold; text-decoration: underline; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; font-size: 14px; margin-bottom: 10px; background: #f1f5f9; padding: 8px; border-left: 4px solid #0d9488; }
            .field-row { display: flex; margin-bottom: 10px; }
            .field-label { font-weight: bold; width: 180px; }
            .field-value { flex: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; min-height: 20px; }
            .notes-section { margin-top: 20px; }
            .notes-field { margin-bottom: 15px; }
            .notes-label { font-weight: bold; margin-bottom: 5px; }
            .notes-value { border: 1px solid #ddd; padding: 10px; min-height: 60px; background: #fafafa; white-space: pre-wrap; }
            .attendance-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .attendance-table th, .attendance-table td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            .attendance-table th { background: #f1f5f9; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; text-align: center; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">Summary Report of Meeting</div>
            <div class="report-title">QUARTERLY MEETING</div>
          </div>
          
          <div class="section">
            <div class="field-row">
              <span class="field-label">Type of Meeting:</span>
              <span class="field-value">${meeting.meeting_type || "Quarterly Meeting"}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Presenter:</span>
              <span class="field-value">${meeting.presenter || ""}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Date:</span>
              <span class="field-value">${meeting.meeting_date || ""}</span>
            </div>
          </div>

          <div class="notes-section">
            <div class="section-title">MEETING NOTES</div>
            
            <div class="notes-field">
              <div class="notes-label">METHOD OF PRESENTATION:</div>
              <div class="notes-value">${meeting.method_of_presentation || ""}</div>
            </div>
            
            <div class="notes-field">
              <div class="notes-label">SUBJECTS COVERED:</div>
              <div class="notes-value">${meeting.subjects_covered || meeting.topics_discussed || meeting.notes || ""}</div>
            </div>
            
            <div class="notes-field">
              <div class="notes-label">REACTION FROM PARTICIPANTS:</div>
              <div class="notes-value">${meeting.reaction_from_participants || ""}</div>
            </div>
            
            <div class="notes-field">
              <div class="notes-label">SUMMARY AND CONCLUSIONS:</div>
              <div class="notes-value">${meeting.summary_and_conclusions || meeting.action_items || ""}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">IN ATTENDANCE (Please Sign)</div>
            <table class="attendance-table">
              <thead>
                <tr>
                  <th style="width: 50%">Name</th>
                  <th style="width: 50%">Title</th>
                </tr>
              </thead>
              <tbody>
                ${attendees.map(a => `
                  <tr>
                    <td>${a.name || ""}</td>
                    <td>${a.title || ""}</td>
                  </tr>
                `).join("")}
                ${attendees.length < 8 ? Array(8 - attendees.length).fill("").map(() => `
                  <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                `).join("") : ""}
              </tbody>
            </table>
          </div>

          <div class="footer">
            Generated: ${new Date().toLocaleDateString()}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleUploadQuarterlyDoc = async (meetingId) => {
    if (!quarterlyFile) {
      toast.error("Please select a file");
      return;
    }

    setUploadingQuarterlyDoc(meetingId);
    try {
      const formData = new FormData();
      formData.append('file', quarterlyFile);

      await axios.post(`${API}/quarterly-meetings/${meetingId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Document uploaded!");
      setQuarterlyFile(null);
      if (quarterlyFileRef.current) quarterlyFileRef.current.value = "";
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingQuarterlyDoc(null);
    }
  };

  const handleDeleteQuarterlyMeeting = async (meetingId) => {
    try {
      await axios.delete(`${API}/quarterly-meetings/${meetingId}`);
      toast.success("Meeting deleted");
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to delete meeting:", error);
      toast.error("Failed to delete meeting");
    }
  };

  const handleDeleteQuarterlyDoc = async (meetingId, docId) => {
    try {
      await axios.delete(`${API}/quarterly-meetings/${meetingId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleUpdateNextMeetingDate = async (meetingId, nextDate) => {
    try {
      await axios.patch(`${API}/quarterly-meetings/${meetingId}`, {
        next_meeting_date: nextDate
      });
      toast.success("Next meeting date updated!");
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to update next meeting date:", error);
      toast.error("Failed to update");
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  };

  // Filter appointments for admin calendar view
  // Only show assessments, MD appointments, and follow-ups - NOT regular caregiver visits
  const getAppointmentsForDay = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return appointments.filter((appt) => {
      if (appt.scheduled_date !== dateStr) return false;
      
      // Show RN assessments
      if (appt.is_assessment) return true;
      
      // Show MD appointments and follow-ups
      if (appt.appointment_type === "md_appointment" || appt.appointment_type === "follow_up") return true;
      
      // Hide regular caregiver/client visits from admin calendar
      // These are visible on individual Client and Employee calendars
      if (appt.appointment_type === "visit") return false;
      
      // Show any other types (future-proofing)
      return true;
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setNewAppointment((prev) => ({
      ...prev,
      scheduled_date: format(date, "yyyy-MM-dd"),
    }));
    setValidationResult(null);
  };

  // Validate appointment against service order limits
  const validateAppointment = async (clientId, scheduledDate, durationMinutes) => {
    if (!clientId || !scheduledDate) {
      setValidationResult(null);
      setClientUsage(null);
      return;
    }

    setValidating(true);
    try {
      const [validationRes, usageRes] = await Promise.all([
        axios.get(`${API}/appointments/validate-service-order`, {
          params: {
            client_id: clientId,
            scheduled_date: scheduledDate,
            duration_minutes: durationMinutes
          }
        }),
        axios.get(`${API}/appointments/client/${clientId}/usage`)
      ]);
      
      setValidationResult(validationRes.data);
      setClientUsage(usageRes.data);
    } catch (error) {
      console.error("Validation failed:", error);
      setValidationResult(null);
      setClientUsage(null);
    } finally {
      setValidating(false);
    }
  };

  // Debounced validation when appointment details change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (dialogOpen && newAppointment.client_id && newAppointment.scheduled_date) {
        validateAppointment(
          newAppointment.client_id,
          newAppointment.scheduled_date,
          newAppointment.duration_minutes
        );
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newAppointment.client_id, newAppointment.scheduled_date, newAppointment.duration_minutes, dialogOpen]);

  const handleCreateAppointment = async (e) => {
    e.preventDefault();

    if (!newAppointment.client_id || !newAppointment.scheduled_date || !newAppointment.scheduled_time) {
      toast.error("Please fill in required fields");
      return;
    }

    // Check if there are validation errors
    if (validationResult && !validationResult.valid && validationResult.errors?.length > 0) {
      toast.error("Cannot schedule: exceeds service order limits");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/appointments`, newAppointment);
      
      // Show success with warnings if any
      if (validationResult?.warnings?.length > 0) {
        toast.success("Appointment scheduled with warnings", {
          description: validationResult.warnings[0]
        });
      } else {
        toast.success("Appointment scheduled successfully!");
      }
      
      setDialogOpen(false);
      setNewAppointment({
        client_id: "",
        employee_id: "",
        appointment_type: "visit",
        scheduled_date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
        scheduled_time: "09:00",
        duration_minutes: 60,
        notes: "",
      });
      setValidationResult(null);
      setClientUsage(null);
      fetchData();
    } catch (error) {
      console.error("Failed to create appointment:", error);
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(detail.message || "Failed to schedule appointment", {
          description: detail.errors?.[0]
        });
      } else {
        toast.error(detail || "Failed to schedule appointment");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await axios.patch(`${API}/appointments/${appointmentId}`, { status });
      toast.success("Appointment updated!");
      fetchData();
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success("Appointment deleted!");
      fetchData();
    } catch (error) {
      console.error("Failed to delete appointment:", error);
      toast.error("Failed to delete appointment");
    }
  };

  const days = getDaysInMonth();
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const firstDayOfMonth = startOfMonth(currentDate).getDay();

  return (
    <Layout>
      {/* Page Header */}
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Schedule</h1>
          <p className="page-subtitle">
            Manage assessments, MD appointments, and follow-ups.
            <span className="text-slate-400 ml-1">
              (Caregiver visits are on Client & Employee calendars)
            </span>
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="new-appointment-btn">
              <Plus className="w-4 h-4" />
              New Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule Appointment</DialogTitle>
              <DialogDescription>
                Create a new appointment for a client.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAppointment}>
              <div className="space-y-4 py-4">
                {/* Client Selection */}
                <div className="space-y-2">
                  <Label htmlFor="client">Client *</Label>
                  <Select
                    value={newAppointment.client_id}
                    onValueChange={(value) =>
                      setNewAppointment((prev) => ({ ...prev, client_id: value }))
                    }
                  >
                    <SelectTrigger data-testid="appt-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Employee Selection */}
                <div className="space-y-2">
                  <Label htmlFor="employee">Assign To</Label>
                  <Select
                    value={newAppointment.employee_id}
                    onValueChange={(value) =>
                      setNewAppointment((prev) => ({ ...prev, employee_id: value }))
                    }
                  >
                    <SelectTrigger data-testid="appt-employee-select">
                      <SelectValue placeholder="Select an employee (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Appointment Type */}
                <div className="space-y-2">
                  <Label htmlFor="type">Appointment Type</Label>
                  <Select
                    value={newAppointment.appointment_type}
                    onValueChange={(value) =>
                      setNewAppointment((prev) => ({ ...prev, appointment_type: value }))
                    }
                  >
                    <SelectTrigger data-testid="appt-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {appointmentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newAppointment.scheduled_date}
                      onChange={(e) =>
                        setNewAppointment((prev) => ({
                          ...prev,
                          scheduled_date: e.target.value,
                        }))
                      }
                      required
                      data-testid="appt-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time">Time *</Label>
                    <Input
                      id="time"
                      type="time"
                      value={newAppointment.scheduled_time}
                      onChange={(e) =>
                        setNewAppointment((prev) => ({
                          ...prev,
                          scheduled_time: e.target.value,
                        }))
                      }
                      required
                      data-testid="appt-time-input"
                    />
                  </div>
                </div>

                {/* Duration */}
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Select
                    value={String(newAppointment.duration_minutes)}
                    onValueChange={(value) =>
                      setNewAppointment((prev) => ({
                        ...prev,
                        duration_minutes: parseInt(value),
                      }))
                    }
                  >
                    <SelectTrigger data-testid="appt-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="180">3 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="300">5 hours</SelectItem>
                      <SelectItem value="360">6 hours</SelectItem>
                      <SelectItem value="480">8 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Service Order Validation */}
                {newAppointment.client_id && newAppointment.scheduled_date && (
                  <div className="space-y-3 pt-2 border-t border-slate-100" data-testid="service-order-validation">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      {validating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Validating service order...
                        </>
                      ) : (
                        <>
                          <Info className="w-4 h-4" />
                          Service Order Validation
                        </>
                      )}
                    </div>

                    {!validating && validationResult && (
                      <>
                        {/* Service Order Info */}
                        {validationResult.has_service_order ? (
                          <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-2">
                            <div className="flex justify-between">
                              <span className="text-slate-500">Authorization:</span>
                              <span className="font-medium">{validationResult.service_order_summary?.authorization_number || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Payer:</span>
                              <span className="font-medium">{validationResult.service_order_summary?.payer_type || "N/A"}</span>
                            </div>
                            {clientUsage?.usage && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Hours Used:</span>
                                  <span className="font-medium">
                                    {clientUsage.usage.total_hours_used} / {clientUsage.usage.total_hours_authorized || "∞"} hrs
                                  </span>
                                </div>
                                {clientUsage.usage.hours_remaining !== null && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Hours Remaining:</span>
                                    <span className={`font-medium ${clientUsage.usage.hours_remaining < 5 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                      {clientUsage.usage.hours_remaining} hrs
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span className="text-slate-500">This Week:</span>
                                  <span className="font-medium">
                                    {clientUsage.usage.weekly_hours_used} / {clientUsage.usage.weekly_hours_limit || "∞"} hrs
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-amber-800">
                                <p className="font-medium">No Active Service Order</p>
                                <p className="mt-1">This client has no active service order. Scheduling without authorization limits.</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Validation Errors */}
                        {validationResult.errors?.length > 0 && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <X className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-red-800">
                                <p className="font-medium">Cannot Schedule - Exceeds Limits</p>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                  {validationResult.errors.map((error, idx) => (
                                    <li key={idx}>{error}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Validation Warnings */}
                        {validationResult.warnings?.length > 0 && validationResult.valid && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-amber-800">
                                <p className="font-medium">Warning - Approaching Limits</p>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                  {validationResult.warnings.map((warning, idx) => (
                                    <li key={idx}>{warning}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* All Good */}
                        {validationResult.valid && validationResult.has_service_order && !validationResult.warnings?.length && (
                          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs text-emerald-800 font-medium">Within service order limits</span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any additional notes..."
                    value={newAppointment.notes}
                    onChange={(e) =>
                      setNewAppointment((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    rows={2}
                    data-testid="appt-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setValidationResult(null);
                    setClientUsage(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={creating || validating || (validationResult && !validationResult.valid)} 
                  data-testid="create-appt-btn"
                >
                  {creating ? "Scheduling..." : validating ? "Validating..." : "Schedule Appointment"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-slate-900">
              {format(currentDate, "MMMM yyyy")}
            </h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevMonth}
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : (
            <>
              {/* Week Days Header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-semibold text-slate-500 py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before the first of the month */}
                {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                  <div
                    key={`empty-${index}`}
                    className="h-24 bg-slate-50 rounded-lg"
                  />
                ))}

                {/* Days of the month */}
                {days.map((day) => {
                  const dayAppointments = getAppointmentsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  const isSelected = selectedDate && isSameDay(day, selectedDate);

                  return (
                    <div
                      key={day.toISOString()}
                      className={`h-24 rounded-lg border p-1 cursor-pointer transition-colors ${
                        isToday
                          ? "border-primary bg-primary/5"
                          : isSelected
                          ? "border-primary/50 bg-primary/10"
                          : "border-slate-100 hover:border-slate-300"
                      }`}
                      onClick={() => handleDateClick(day)}
                      data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                    >
                      <div
                        className={`text-sm font-medium mb-1 ${
                          isToday ? "text-primary" : "text-slate-700"
                        }`}
                      >
                        {format(day, "d")}
                      </div>
                      <div className="space-y-0.5 overflow-hidden">
                        {dayAppointments.slice(0, 2).map((appt) => (
                          <div
                            key={appt.id}
                            className={`text-xs px-1 py-0.5 rounded truncate border ${
                              appt.assessment_kind
                                ? `${APPT_KIND_COLOR[appt.assessment_kind]} font-medium`
                                : appt.is_assessment 
                                ? "bg-indigo-100 text-indigo-700 border-indigo-200 font-medium"
                                : appointmentTypeColors[appt.appointment_type] ||
                                  "bg-slate-100 text-slate-700"
                            }`}
                            title={appt.assessment_kind ? APPT_KIND_LABEL[appt.assessment_kind] : appt.is_assessment ? "RN Assessment" : appt.appointment_type}
                          >
                            {appt.is_assessment && !appt.assessment_kind && "📋 "}
                            {appt.scheduled_time} {appt.client_name?.split(" ")[0]}
                          </div>
                        ))}
                        {dayAppointments.length > 2 && (
                          <div className="text-xs text-slate-500 px-1">
                            +{dayAppointments.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Sidebar - Selected Day Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {selectedDate
              ? format(selectedDate, "EEEE, MMMM d")
              : "Select a date"}
          </h3>

          {selectedDate && (
            <>
              <Button
                size="sm"
                className="w-full mb-4 gap-2"
                onClick={() => {
                  setNewAppointment((prev) => ({
                    ...prev,
                    scheduled_date: format(selectedDate, "yyyy-MM-dd"),
                  }));
                  setDialogOpen(true);
                }}
                data-testid="add-appt-selected-date"
              >
                <Plus className="w-4 h-4" />
                Add Appointment
              </Button>

              <div className="space-y-3">
                {getAppointmentsForDay(selectedDate).length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No appointments scheduled
                  </p>
                ) : (
                  getAppointmentsForDay(selectedDate).map((appt) => (
                    <div
                      key={appt.id}
                      className={`p-3 rounded-lg border transition-colors ${
                        appt.assessment_kind
                          ? "border-rose-200 bg-rose-50/40 hover:border-rose-300"
                          : appt.is_assessment 
                          ? "border-indigo-200 bg-indigo-50/50 hover:border-indigo-300"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`schedule-appt-${appt.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${
                            appt.assessment_kind
                              ? APPT_KIND_COLOR[appt.assessment_kind]
                              : appt.is_assessment
                              ? "bg-indigo-100 text-indigo-700 border-indigo-200"
                              : appointmentTypeColors[appt.appointment_type]
                          }`}
                          data-testid={`schedule-appt-type-${appt.id}`}
                        >
                          {appt.assessment_kind
                            ? APPT_KIND_LABEL[appt.assessment_kind]
                            : appt.is_assessment 
                            ? "RN Assessment"
                            : appointmentTypes.find(
                                (t) => t.value === appt.appointment_type
                              )?.label || appt.appointment_type}
                        </span>
                        {(!appt.is_assessment || appt.assessment_kind) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleUpdateStatus(appt.id, "completed")
                                }
                              >
                                Mark Completed
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleUpdateStatus(appt.id, "cancelled")
                                }
                              >
                                Cancel
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteAppointment(appt.id)}
                                className="text-red-600"
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <p className="font-medium text-slate-900 text-sm">
                        {appt.client_name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                        <Clock className="w-3 h-3" />
                        {appt.scheduled_time} ({appt.duration_minutes} min)
                      </div>
                      {appt.employee_name && appt.employee_name !== "Unassigned" && (
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                          <User className="w-3 h-3" />
                          {appt.is_assessment ? `RN: ${appt.employee_name}` : appt.employee_name}
                        </div>
                      )}
                      {appt.is_assessment && (!appt.employee_name || appt.employee_name === "Unassigned") && (
                        <div className="flex items-center gap-2 text-xs text-amber-600 mt-1">
                          <User className="w-3 h-3" />
                          RN: Unassigned
                        </div>
                      )}
                      {appt.notes && (
                        <p className="text-xs text-slate-500 mt-2 italic">
                          {appt.notes}
                        </p>
                      )}
                      {appt.is_assessment && !appt.assessment_kind && (
                        <p className="text-xs text-indigo-600 mt-2 font-medium">
                          Referral Assessment - Manage in Referrals page
                        </p>
                      )}
                      {appt.assessment_kind && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <VisitClockButton appointment={appt} onChanged={fetchData} />
                          <AppointmentAssessmentButton appointment={appt} onChanged={fetchData} />
                          {appt.status === "completed" && <span className="text-xs text-emerald-700 font-medium">Assessment completed</span>}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        {appointmentTypes.map((type) => (
          <div key={type.value} className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${
                appointmentTypeColors[type.value]?.split(" ")[0]
              }`}
            />
            <span className="text-slate-600">{type.label}</span>
          </div>
        ))}
      </div>

      {/* Quarterly Meetings Section - Admin Only */}
      {user?.role === "admin" && (
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Quarterly Meetings
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                  Admin Only
                </span>
              </h2>
              <p className="text-sm text-slate-500 mt-1">Schedule and manage quarterly review meetings</p>
            </div>
            <Button
              onClick={() => {
                resetQuarterlyForm();
                setQuarterlyDialogOpen(true);
              }}
              className="gap-2"
              data-testid="schedule-quarterly-btn"
            >
              <Plus className="w-4 h-4" />
              Schedule Meeting
            </Button>
          </div>

          {quarterlyMeetings.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No quarterly meetings scheduled</p>
              <p className="text-sm mt-1">Click &quot;Schedule Meeting&quot; to create your first quarterly meeting</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quarterlyMeetings.map((meeting) => (
                <div
                  key={meeting.id}
                  className="p-4 border border-slate-200 rounded-lg bg-white hover:border-primary/30 hover:shadow-sm transition-all"
                  data-testid={`quarterly-meeting-${meeting.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center">
                        <span className="text-xs font-bold text-primary">
                          {new Date(meeting.meeting_date).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <span className="text-xl font-bold text-primary leading-none">
                          {new Date(meeting.meeting_date).getDate()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">
                          {meeting.meeting_type || "Quarterly Meeting"}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(meeting.meeting_date).toLocaleDateString('en-US', { 
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-slate-500 hover:text-primary hover:bg-primary/10"
                        onClick={() => handlePrintQuarterlyMeeting(meeting)}
                        title="Print Report"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteQuarterlyMeeting(meeting.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Meeting Details */}
                  {meeting.presenter && (
                    <p className="text-sm text-slate-600 mb-1">
                      <span className="font-medium">Presenter:</span> {meeting.presenter}
                    </p>
                  )}
                  
                  {(meeting.subjects_covered || meeting.notes) && (
                    <p className="text-sm text-slate-600 mb-2 bg-slate-50 p-2 rounded line-clamp-2">
                      {meeting.subjects_covered || meeting.notes}
                    </p>
                  )}

                  {/* Attendees count */}
                  {meeting.attendees_list && meeting.attendees_list.length > 0 && meeting.attendees_list.some(a => a.name) && (
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {meeting.attendees_list.filter(a => a.name).length} attendees
                    </p>
                  )}

                  {/* Next Meeting Date */}
                  <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-blue-700">Next:</span>
                    <Input
                      type="date"
                      className="h-7 text-sm flex-1"
                      value={meeting.next_meeting_date || ""}
                      onChange={(e) => handleUpdateNextMeetingDate(meeting.id, e.target.value)}
                    />
                  </div>

                  {/* Documents Section */}
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                      <FolderOpen className="w-3 h-3" />
                      Documents ({meeting.documents?.length || 0})
                    </p>
                    
                    {meeting.documents?.length > 0 && (
                      <div className="space-y-1 mb-2 max-h-24 overflow-y-auto">
                        {meeting.documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded text-xs"
                          >
                            <span className="flex items-center gap-1 text-slate-700 truncate flex-1 mr-2">
                              <File className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              <span className="truncate">{doc.filename}</span>
                            </span>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => window.open(`${API}/quarterly-meetings/${meeting.id}/documents/${doc.id}`, '_blank')}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteQuarterlyDoc(meeting.id, doc.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Document */}
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        className="h-8 text-xs flex-1"
                        onChange={(e) => setQuarterlyFile(e.target.files[0])}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs gap-1 flex-shrink-0"
                        onClick={() => handleUploadQuarterlyDoc(meeting.id)}
                        disabled={!quarterlyFile || uploadingQuarterlyDoc === meeting.id}
                        data-testid={`upload-quarterly-doc-${meeting.id}`}
                      >
                        <Upload className="w-3 h-3" />
                        {uploadingQuarterlyDoc === meeting.id ? "..." : "Upload"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quarterly Meeting Dialog - Using QuarterlyMeetingDialog component */}
      <QuarterlyMeetingDialog
        open={quarterlyDialogOpen}
        onOpenChange={setQuarterlyDialogOpen}
        clientName="Agency Meeting"
        newQuarterlyMeeting={newQuarterlyMeeting}
        setNewQuarterlyMeeting={setNewQuarterlyMeeting}
        saving={savingQuarterly}
        onSave={handleCreateQuarterlyMeeting}
        editingMeetingId={editingQuarterlyId}
      />
    </Layout>
  );
}
